import { Table, Text, Tooltip } from 'octiv-components';
import { useActiveUserTenant } from 'octiv-context/ActiveUserTenant';
import {
  getDateReadableDayMonthYear,
  getUserPackageInfo,
  translateSessionsAvailableText,
} from 'octiv-utilities/Functions';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default ({ data, onClickAction, isCard, ...props }) => {
  const { t } = useTranslation();
  const { toCurrency } = useActiveUserTenant();
  const tableData = useMemo(() => data || [], [data]);

  const tableColumns = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        accessor: 'package',
        Cell: ({ value }) => value?.name || null,
      },
      {
        Header: t('start'),
        accessor: 'startDate',
        Cell: ({ value }) => getDateReadableDayMonthYear({ date: value }),
      },
      {
        Header: t('end'),
        accessor: 'endDate',
        Cell: ({ value }) => getDateReadableDayMonthYear({ date: value }),
      },
      {
        Header: t('sessionsAvailable'),
        accessor: 'sessionsAvailableText',
        Cell: ({ value }) => translateSessionsAvailableText({ text: value }),
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { package: tenantPackage, isActive },
          },
        }) => (
          <>
            <Text as='span'>{isActive ? t('active') : t('inactive')}</Text>
            {tenantPackage.isActive ? (
              ''
            ) : (
              <Text as='span' color='danger'>
                {` (${t('mainPackageDeactivated')})`}
              </Text>
            )}
          </>
        ),
      },
      {
        Header: t('price'),
        id: 'price',
        Cell: ({ row: { original } }) => {
          const packagePrice = original?.package?.price || 0;

          // Only show discounted prices for card users with discount information
          if (isCard && original.discount) {
            const { discount } = original;

            if (discount.type === 'specialRate') {
              // Special rate shows the original price with strikethrough and then the special rate
              const specialRate = parseFloat(discount.value) || 0;
              return (
                <>
                  <Text as='span' mr={1} textDecoration={'line-through'}>
                    {toCurrency({ value: packagePrice })}
                  </Text>

                  <Text as='span' color='primary'>
                    {toCurrency({ value: specialRate })}
                  </Text>
                </>
              );
            }
            if (discount.type === 'discount' && discount.meta) {
              if (discount.meta.type === 'percentage') {
                // Calculate discounted price with percentage
                const percentageAmount = parseFloat(discount.meta.amount) || 0;
                const discountedPrice =
                  packagePrice - (packagePrice * percentageAmount) / 100;

                return (
                  <>
                    <Text as='span' mr={1} textDecoration={'line-through'}>
                      {toCurrency({ value: packagePrice })}
                    </Text>

                    <Text as='span' color='primary'>
                      {toCurrency({ value: discountedPrice })}
                      <Text
                        as='span'
                        color='grey2'
                        fontSize={'0.8em'}
                      >{` (${discount.meta.amount}% off)`}</Text>
                    </Text>
                  </>
                );
              }
              if (discount.meta.type === 'fixed') {
                // Calculate discounted price with fixed amount
                const fixedAmount = parseFloat(discount.meta.amount) || 0;
                const discountedPrice = Math.max(0, packagePrice - fixedAmount);

                return (
                  <>
                    <Text as='span' mr={1} textDecoration={'line-through'}>
                      {toCurrency({ value: packagePrice })}
                    </Text>

                    <Text as='span' color='primary'>
                      {toCurrency({ value: discountedPrice })}
                      <Text
                        as='span'
                        color='grey2'
                        fontSize={'0.8em'}
                      >{` (${toCurrency({ value: fixedAmount })} off)`}</Text>
                    </Text>
                  </>
                );
              }
            }
          }

          // Default case: just show regular price
          return toCurrency({ value: packagePrice });
        },
      },
      {
        Header: t('actions'),
        id: 'actions',
        Cell: ({
          row: {
            original,
            original: { id, isActive, package: tenantPackage },
          },
        }) => {
          const { canTopUp } = getUserPackageInfo({
            userPackage: original,
          });
          return (
            <Tooltip
              options={[
                ...(tenantPackage.isActive
                  ? [{ label: t('edit'), value: 'edit' }]
                  : []),
                ...(isActive
                  ? [
                      ...(canTopUp
                        ? [{ label: t('topUp'), value: 'topUp' }]
                        : []),
                      { label: t('deactivate'), value: 'deactivate' },
                    ]
                  : [{ label: t('delete'), value: 'delete' }]),
              ]}
              placement='left'
              onClick={({ value }) => onClickAction({ action: value, id })}
            />
          );
        },
      },
    ],
    [onClickAction, toCurrency, t, isCard] // Add isCardUser as a dependency
  );

  return (
    <Table
      {...props}
      hasAlternateBackground
      columns={tableColumns}
      data={tableData}
    />
  );
};
